import {
  Button,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { ChevronRight } from "lucide-react";
import type { EmployeeEntitlementSummaryRow } from "@/modules/hr/leave/domain/types";

type LeaveEntitlementsByEmployeeTableProps = {
  data: EmployeeEntitlementSummaryRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewDetail: (row: EmployeeEntitlementSummaryRow) => void;
};

export function LeaveEntitlementsByEmployeeTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewDetail,
}: LeaveEntitlementsByEmployeeTableProps) {
  const columns: TanstackReactTable.ColumnDef<EmployeeEntitlementSummaryRow>[] =
    [
      createSortableColumn<EmployeeEntitlementSummaryRow>(
        "employeeName",
        "ພະນັກງານ",
        {
          size: 220,
          cell: ({ row }) => (
            <div>
              <div className="font-medium">{row.original.employeeName}</div>
              <div className="text-muted-foreground text-xs">
                {row.original.employeeEmail}
              </div>
            </div>
          ),
        },
      ),
      createSortableColumn<EmployeeEntitlementSummaryRow>("year", "ປີ", {
        size: 80,
      }),
      createSortableColumn<EmployeeEntitlementSummaryRow>(
        "totalDaysCanLeave",
        "ວັນລາທີ່ມີສິດ (ລວມ)",
        {
          size: 140,
          cell: ({ row }) => (
            <span className="font-medium">
              {row.original.totalDaysCanLeave}
            </span>
          ),
        },
      ),
      createSortableColumn<EmployeeEntitlementSummaryRow>(
        "totalDaysUsed",
        "ວັນລາທີ່ໃຊ້ແລ້ວ (ລວມ)",
        {
          size: 140,
          cell: ({ row }) => (
            <span className="font-medium">{row.original.totalDaysUsed}</span>
          ),
        },
      ),
      createSortableColumn<EmployeeEntitlementSummaryRow>(
        "totalRemaining",
        "ຄົງເຫຼືອ (ລວມ)",
        {
          size: 120,
          cell: ({ row }) => (
            <span
              className={
                row.original.totalRemaining < 0
                  ? "font-medium text-destructive"
                  : "font-medium text-primary"
              }
            >
              {row.original.totalRemaining}
            </span>
          ),
        },
      ),
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDetail(row.original)}
            aria-label="ເບິ່ງລາຍລະອຽດສິດລາຕໍ່ປະເພດ"
          >
            ລາຍລະອຽດ
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        ),
        size: 120,
      },
    ];

  return (
    <DataTable<EmployeeEntitlementSummaryRow, unknown>
      noDataMessage="ບໍ່ພົບສິດລາຕໍ່ປີ. ກະລຸນາເລືອກປີແລະນຳໃຊ້ກັ່ນຕອງ."
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id ?? "employeeName", !!sorting[0]?.desc);
      }}
    />
  );
}
